import {
    AppDispatch,
    RootState,
} from '../../store';
import {
    addListener,
    bindActionCreators,
    createListenerMiddleware,
    isAnyOf,
} from '@reduxjs/toolkit';
import {
    addMonthlyDiscount,
    addOneTimeDiscount,
    goToBasket,
    selectAtomicId,
    selectDeviceId,
    selectHardwareOnly,
    selectInsurance,
    selectRedPlusIds,
    selectSelectedCategories,
    selectSelectedProducts,
    selectSimOnly,
    setInitialState,
    setProducts,
    toggleCategory,
    toggleProduct,
} from './slice';
import {
    selectProducts,
    selectTrackingPayload,
} from './selectors';
import {
    getDeviceDetailAtomic,
    getDeviceDetailGroup,
    getProducts,
    getSimOnly,
    getTariffWithHardware,
} from './api/glados';
import {
    createPageViewTrackingDataWithError,
    createPageViewTrackingPayload,
    trackToggleCategory,
    trackToggleProduct,
} from './helpers/tracking';
import {
    TrackingError,
    TrackingErrorType,
    trackIt,
    TrackType,
} from '@vfde-sails/tracking';
import {
    isUpsellItem,
    UpsellFacetValue,
} from '@vfde-sails/glados-v2';
import {
    BASKET_PROPOSITION_BNT,
    BASKET_PROPOSITION_HARDWAREONLY,
    BasketItem,
    createBasket,
    createBasketItem,
    createBasketProposition,
    createSailsReadyEvent,
    createUrlWithQueryString,
    generateDeeplink,
    updateUrl,
} from '@vfde-sails/utils';
import {
    getSessionStorageItemJson,
    updateStorage,
} from '@vfde-sails/storage';
import {
    RedplusTariff,
    SAILS_BNT_STORAGE,
    SAILS_PARAM_INSURANCE,
    SAILS_PARAM_RED_PLUS_IDS,
} from '@vfde-sails/constants';
import { APP } from 'Constant';
import { SailsConnectMoreStorage } from './interfaces/storage';

const appListener = createListenerMiddleware();

export default appListener;

export const startAppListening = appListener.startListening.withTypes<
    RootState,
    AppDispatch
>();

export const addAppListener = addListener.withTypes<RootState, AppDispatch>();

/**
 * setInitialState listener
 */
startAppListening({
    actionCreator: setInitialState.fulfilled,
    effect: async (_action, api) => {
        api.cancelActiveListeners();

        const [
            deviceId,
            isSimOnly,
            isHardwareOnly,
            atomicId,
        ] = [
            selectDeviceId(api.getState()),
            selectSimOnly(api.getState()),
            selectHardwareOnly(api.getState()),
            selectAtomicId(api.getState()),
        ];

        if (deviceId && !atomicId) {
            // Call hardware group service to get atomic id to handle this edge case
            await api.dispatch(getDeviceDetailGroup.initiate());
        }

        switch (true) {
            case isHardwareOnly:
                await api.dispatch(getDeviceDetailAtomic.initiate());
                break;
            case isSimOnly:
                await api.dispatch(getSimOnly.initiate());
                break;
            default:
                await api.dispatch(getTariffWithHardware.initiate());
                break;
        }

        api.dispatch(getProducts.initiate());
    },
});

startAppListening({
    matcher: getProducts.matchFulfilled,
    effect: (_action, api) => {
        api.cancelActiveListeners();

        const [
            insuranceId,
            redPlusIds,
            products,
            selectedProducts,
            trackingData,
        ] = [
            selectInsurance(api.getState()),
            selectRedPlusIds(api.getState()),
            selectProducts(api.getState()),
            selectSelectedProducts(api.getState()),
            selectTrackingPayload(api.getState()),
        ];

        // Track page view
        const trackingPayload = createPageViewTrackingPayload(
            trackingData,
            getSessionStorageItemJson<SailsConnectMoreStorage>(SAILS_BNT_STORAGE),
        );

        trackIt(TrackType.PageView, trackingPayload);

        // Update products with data from API
        api.dispatch(setProducts(products.filter(product => {
            if (product.category.value === UpsellFacetValue.Insurance && insuranceId === product.id) {
                return true;
            }

            if (product.category.value === UpsellFacetValue.Redplus && redPlusIds.indexOf(`${product.id}`) !== -1) {
                return true;
            }

            return selectedProducts?.some(selectedProduct => selectedProduct.id === product.id);
        })));

        // Trigger Sails ready event for target
        const actions = bindActionCreators({
            addOneTimeDiscount,
            addMonthlyDiscount,
        }, api.dispatch);

        createSailsReadyEvent([
            {
                actions,
                applicationScope: APP,
            },
        ]);
    },
});

// Separate constant because of inference problem in RTK 2 (see https://github.com/reduxjs/redux-toolkit/discussions/4018)
const isAnyError = isAnyOf(
    getProducts.matchRejected,
    getSimOnly.matchRejected,
    getTariffWithHardware.matchRejected,
    getDeviceDetailGroup.matchRejected,
    getDeviceDetailAtomic.matchRejected,
);

startAppListening({
    matcher: isAnyError,
    effect: (action, api) => {
        api.cancelActiveListeners();

        const trackingData = selectTrackingPayload(api.getState());
        const trackingError: TrackingError = {
            /* eslint-disable camelcase, @typescript-eslint/naming-convention */
            error_type: TrackingErrorType.Technical,
            error_message: action.payload!.message,
            error_code: action.payload!.status?.toString(),
            error_location: action.meta.arg.endpointName,
            /* eslint-enable camelcase, @typescript-eslint/naming-convention */
        };

        const pageViewTrackingDataWithError = createPageViewTrackingDataWithError(
            trackingData,
            getSessionStorageItemJson<SailsConnectMoreStorage>(SAILS_BNT_STORAGE),
            trackingError,
        );

        trackIt(
            TrackType.PageView,
            pageViewTrackingDataWithError,
        );
    },
});

startAppListening({
    actionCreator: goToBasket.fulfilled,
    effect: (action, api) => {
        api.cancelActiveListeners();

        const redPlusItems: BasketItem[] = [];
        const serviceIds: string[] = [];
        const hardwareItems: BasketItem[] = [];

        for (const product of action.payload.selectedProducts) {
            // Continue if product is not fully hydrated
            if (!isUpsellItem(product)) {
                continue;
            }

            switch (product.category.value) {
                case UpsellFacetValue.Redplus:
                    // If we have a tariff, we add the selected family cards to the basket
                    action.payload.atomicTariffId && redPlusItems.push(createBasketItem({ sublevelTariffId: product.basketId }));
                    break;
                case UpsellFacetValue.Insurance:
                    // If we have a device, we add the selected insurance to basket)
                    action.payload.atomicId && serviceIds.push(product.basketId);
                    break;
                case UpsellFacetValue.Accessory:
                    // If we have a device, we add all the selected accessories to the basket
                    action.payload.atomicId && hardwareItems.push(createBasketItem({ atomicDeviceId: product.basketId }));
                    break;
            }
        }

        const propositions = [];
        const basketItem = createBasketItem({
            sublevelTariffId: action.payload.atomicTariffId || undefined,
            atomicDeviceId: action.payload.atomicId || undefined,
            discountIds: action.payload.discounts,
            serviceIds,
            redPlusItems,
        });

        if (action.payload.atomicTariffId) {
            propositions.push(createBasketProposition(BASKET_PROPOSITION_BNT, [basketItem]));

            if (hardwareItems.length > 0) {
                propositions.push(createBasketProposition(BASKET_PROPOSITION_HARDWAREONLY, hardwareItems));
            }
        }
        else {
            propositions.push(createBasketProposition(BASKET_PROPOSITION_HARDWAREONLY, [basketItem, ...hardwareItems]));
        }

        const basket = createBasket(action.payload.salesChannel!, propositions);

        const deeplink = generateDeeplink(basket);

        window.location.assign(deeplink);
    },
});

startAppListening({
    actionCreator: toggleCategory,
    effect: (action, api) => {
        api.cancelActiveListeners();

        const selectedCategories = selectSelectedCategories(api.getState());
        const wasAdded = selectedCategories.some(category => category.value === action.payload.value);

        updateUrl(createUrlWithQueryString('categories', selectedCategories.map(category => category.value).toString()), true);
        trackToggleCategory(wasAdded, action.payload, selectedCategories);
    },
});

startAppListening({
    matcher: isAnyOf(toggleProduct, setProducts),
    effect: (action, api) => {
        api.cancelActiveListeners();

        const selectedProducts = selectSelectedProducts(api.getState());
        const insuranceId = selectInsurance(api.getState());
        const redPlusIds = selectRedPlusIds(api.getState());

        updateStorage<SailsConnectMoreStorage>(SAILS_BNT_STORAGE, {
            [SAILS_PARAM_INSURANCE]: insuranceId,
            [SAILS_PARAM_RED_PLUS_IDS]: redPlusIds as RedplusTariff[],
            [APP]: {
                selectedAccessories: selectedProducts
                    .filter(
                        product => isUpsellItem(product) && product.category.value === UpsellFacetValue.Accessory,
                    )
                    .map(product => product.id),
            },
        }, { shouldDeepMerge: false });

        if (toggleProduct.match(action)) {
            const wasAdded = selectedProducts.some(product => product.id === action.payload.id);
            const addedProducts = wasAdded ? [action.payload] : [];
            const removedProducts = wasAdded ? [] : [action.payload];

            trackToggleProduct(addedProducts, removedProducts);
        }
    },
});
